<!-- The member found, shown the same way when the request is logged and when it is opened again -->
<ng-template #memberCard let-employee>
  <div class="rounded border border-slate-200 bg-slate-50 p-4 md:col-span-2">
    <h3 class="font-semibold text-slate-900">{{ employee.firstName }} {{ employee.surname }}</h3>
    <p class="text-sm text-slate-700">Customer: {{ employee.companyName || 'Not recorded' }}</p>
    <p class="text-sm text-slate-700">
      Employee number {{ employee.employeeNumber || 'N/A' }} · {{ employee.idNumber || employee.nationalIdNumber || 'No ID number' }}
    </p>
    <p class="text-sm text-slate-700">{{ employee.contactNumber || 'No contact number' }} · {{ employee.emailAddress || 'No email address' }}</p>
    @if (ownerDependant(); as dependant) {
      <p class="mt-2 text-sm text-slate-700">The request is for their dependant, {{ dependant.name ?? 'unnamed dependant' }}.</p>
    }
  </div>
</ng-template>

<mat-card class="rounded-lg border border-slate-200 p-6 shadow-sm">
  <mat-card-header class="mb-6">
    <mat-card-title class="text-2xl font-semibold text-slate-900">New request</mat-card-title>
    <mat-card-subtitle>Step 1 of 8 · Who is the request for, and what is being asked?</mat-card-subtitle>
  </mat-card-header>
  <mat-card-content>
    <form [formGroup]="form" (ngSubmit)="submit()" class="grid gap-8">
      <!-- The member: found on the client's list, or typed in when they are not on it; once the request is logged the member is shown, not changed -->
      <section>
        <h2 class="mb-4 text-lg font-semibold text-slate-900">Member</h2>

        @if (isEdit()) {
          <div class="grid gap-4 md:grid-cols-2">
            @if (selectedEmployee(); as employee) {
              <ng-container *ngTemplateOutlet="memberCard; context: { $implicit: employee }" />
            } @else {
              <p class="text-sm text-slate-600 md:col-span-2">Loading the member...</p>
            }
          </div>
        } @else {
          <mat-radio-group [value]="mode()" (change)="setMode($event.value)" aria-label="Member" class="mb-4 flex flex-wrap gap-6">
            <mat-radio-button value="existing">Find an existing member</mat-radio-button>
            <mat-radio-button value="new">Capture a new member</mat-radio-button>
          </mat-radio-group>

          @if (mode() === 'existing') {
            <div class="grid gap-4 md:grid-cols-2">
              <searchable-lookup-select
                formControlName="ownerEmployeeId"
                class="md:col-span-2"
                label="Member"
                placeholder="Search for the member"
                searchPlaceholder="Name, employee number, UID, ID number or mobile number"
                loadingLabel="Searching..."
                requiredError="Choose the member the request is for, or capture a new member."
                [options]="employeeStore.employeeLookupResponses()"
                [loading]="employeeStore.employeeLookupLoading()"
                (selectionChanged)="employeeChanged($event)"
                (searchChanged)="employeeSearchChanged($event)" />

              @if (selectedEmployee(); as employee) {
                <ng-container *ngTemplateOutlet="memberCard; context: { $implicit: employee }" />
              }
            </div>
          } @else {
            <!-- A new member: what is asked for on the call; the rest is completed in the Eligibility step -->
            <div class="grid gap-4 md:grid-cols-2" formGroupName="newMember">
              <lookup-select
                formControlName="clientCompanyId"
                class="md:col-span-2"
                label="Customer"
                [options]="customers()"
                noneLabel="Select the customer"
                requiredError="The customer the member belongs to is required."
                hint="The client and company the member works for." />
              <mat-form-field appearance="outline">
                <mat-label>Name</mat-label>
                <input matInput formControlName="firstName" maxlength="100" />
                @if (newMemberForm.controls.firstName.invalid && newMemberForm.controls.firstName.touched) {
                  <mat-error>Name is required.</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Surname</mat-label>
                <input matInput formControlName="surname" maxlength="100" />
                @if (newMemberForm.controls.surname.invalid && newMemberForm.controls.surname.touched) {
                  <mat-error>Surname is required.</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>ID number</mat-label>
                <input matInput formControlName="idNumber" maxlength="30" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Contact number</mat-label>
                <input matInput formControlName="contactNumber" maxlength="50" />
              </mat-form-field>
              <mat-form-field appearance="outline" class="md:col-span-2">
                <mat-label>Email address</mat-label>
                <input matInput type="email" formControlName="emailAddress" maxlength="320" />
                @if (newMemberForm.controls.emailAddress.invalid && newMemberForm.controls.emailAddress.touched) {
                  <mat-error>Enter a valid email address.</mat-error>
                }
              </mat-form-field>
            </div>
          }
        }

        <div class="mt-4 grid gap-4 md:grid-cols-2">
          <lookup-select
            formControlName="userTypeId"
            label="User type"
            [options]="userTypes()"
            [allowNone]="false"
            [hint]="isEdit() ? 'Fixed once the request is logged.' : null"
            requiredError="The user type is required." />
          @if (isDependantType() && !ownerDependant()) {
            <p class="self-center text-sm text-slate-600">The dependant's details are captured in the Eligibility step.</p>
          }
        </div>
      </section>

      <!-- The member's earlier interventions, so the clinician sees the history before the request is logged -->
      @if ((isEdit() || mode() === 'existing') && selectedEmployee(); as employee) {
        <section>
          <h2 class="mb-4 text-lg font-semibold text-slate-900">Member history</h2>
          <employee-interventions-panel [employeeId]="employee.id" [openInNewTab]="true" />
        </section>
      }

      <section>
        <h2 class="mb-4 text-lg font-semibold text-slate-900">Request</h2>
        <div class="grid gap-4 md:grid-cols-2">
          <mat-form-field appearance="outline">
            <mat-label>Intervention type</mat-label>
            <mat-select formControlName="interventionTypeId">
              <mat-option value="">Select intervention type</mat-option>
              @for (item of interventionTypes(); track item.id) {
                <mat-option [value]="item.id">{{ item.name ?? item.code }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Intervention name</mat-label>
            <input matInput formControlName="interventionName" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="md:col-span-2">
            <mat-label>Description</mat-label>
            <textarea matInput formControlName="description" rows="3"></textarea>
          </mat-form-field>
          <mat-form-field appearance="outline" class="md:col-span-2">
            <mat-label>Reason</mat-label>
            <textarea matInput formControlName="reason" rows="3"></textarea>
          </mat-form-field>
          <!-- Where the request came from and what kind it is -->
          <lookup-select
            formControlName="sourceChannelId"
            label="Source channel"
            [options]="sourceChannels()"
            [allowNone]="false"
            requiredError="Where the request came from is required." />
          <lookup-select
            formControlName="requestTypeId"
            label="Request type"
            [options]="requestTypes()"
            [allowNone]="false"
            requiredError="The kind of request is required." />
          <lookup-select formControlName="priorityId" label="Priority" [options]="priorities()" noneLabel="Not set" />
          <lookup-select formControlName="riskClassificationId" label="Risk classification" [options]="riskRatings()" noneLabel="Not set" />
          <lookup-select formControlName="confidentialityLevelId" label="Confidentiality level" [options]="confidentialityLevels()" noneLabel="Not set" />
          <mat-form-field appearance="outline">
            <mat-label>Start date</mat-label>
            <input matInput [matDatepicker]="startDatePicker" formControlName="startDate" />
            <mat-datepicker-toggle matIconSuffix [for]="startDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #startDatePicker></mat-datepicker>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Target review date</mat-label>
            <input matInput [matDatepicker]="targetReviewDatePicker" formControlName="targetReviewDate" />
            <mat-datepicker-toggle matIconSuffix [for]="targetReviewDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #targetReviewDatePicker></mat-datepicker>
          </mat-form-field>
        </div>
      </section>

      @if (!readOnly()) {
        <div class="flex justify-end pt-4">
          <button mat-flat-button type="submit" [disabled]="busy()">
            @if (busy()) {
              <mat-spinner diameter="20" class="mr-2" />
            }
            {{ isEdit() ? 'Save changes' : 'Create request and continue to Eligibility' }}
          </button>
        </div>
      }
    </form>
  </mat-card-content>
</mat-card>
